/**
 * Las piezas compartidas de las tablas de la consola.
 *
 * 🚨 Existe por dos cosas medidas:
 *
 *  1. **Manifiestos escupía 470 filas de una sola vez: 24.961 px de alto, que
 *     son treinta pantallas de scroll.** Nadie baja treinta pantallas.
 *  2. Luis: «me estás metiendo el mismo diseño de antes». La maqueta que él
 *     aprobó tenía caja, encabezado con fondo y pie con paginación; la consola
 *     tenía una tabla suelta, gris sobre gris.
 *
 * ⛔ La paginación es en el NAVEGADOR, sobre lo que ya llegó. No confundir con
 * los filtros, que van al servidor a propósito: la consulta tiene tope y
 * filtrar aquí dejaría fuera lo que no alcanzó a entrar.
 */

import { useEffect, useMemo, useState } from 'react'

/** Cuántas filas por página. 25 llena una pantalla de PC sin obligar a bajar. */
export const POR_PAGINA = 25

/**
 * Parte una lista en páginas.
 *
 * Se devuelve a la página 1 sola cuando cambia el tamaño de la lista: si no,
 * al filtrar quedabas en la página 8 de una lista que ahora tiene 2 y la
 * pantalla se veía vacía sin explicación.
 */
export function usarPaginas<T>(todo: T[], porPagina = POR_PAGINA) {
  const [pagina, setPagina] = useState(1)
  const paginas = Math.max(1, Math.ceil(todo.length / porPagina))

  useEffect(() => {
    setPagina(1)
  }, [todo.length])

  const enPantalla = useMemo(
    () => todo.slice((pagina - 1) * porPagina, pagina * porPagina),
    [todo, pagina, porPagina],
  )

  return {
    enPantalla,
    pagina,
    paginas,
    irA: (p: number) => setPagina(Math.min(paginas, Math.max(1, p))),
    desde: todo.length === 0 ? 0 : (pagina - 1) * porPagina + 1,
    hasta: Math.min(pagina * porPagina, todo.length),
    total: todo.length,
  }
}

/** «1 · 2 · 3 · … · 19». Nunca pinta más de siete botones. */
function cualesPaginas(actual: number, total: number): (number | '…')[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1)
  if (actual <= 4) return [1, 2, 3, 4, 5, '…', total]
  if (actual >= total - 3) return [1, '…', total - 4, total - 3, total - 2, total - 1, total]
  return [1, '…', actual - 1, actual, actual + 1, '…', total]
}

export function PieDeTabla({
  desde, hasta, total, pagina, paginas, irA, queSon = 'filas',
}: {
  desde: number
  hasta: number
  total: number
  pagina: number
  paginas: number
  irA: (p: number) => void
  queSon?: string
}) {
  // Con una sola página el pie sigue sirviendo: dice cuántas hay en total.
  return (
    <div className="pie-tabla">
      <span>
        {total === 0 ? (
          `Sin ${queSon}`
        ) : (
          <>
            Mostrando <b className="num">{desde}</b>–<b className="num">{hasta}</b> de{' '}
            <b className="num">{total.toLocaleString('es-CO')}</b> {queSon}
          </>
        )}
      </span>

      {paginas > 1 && (
        <div className="paginas">
          <button onClick={() => irA(pagina - 1)} disabled={pagina === 1} aria-label="Página anterior">
            ‹
          </button>
          {cualesPaginas(pagina, paginas).map((p, i) =>
            p === '…' ? (
              <button key={`h${i}`} className="pag-hueco" disabled>
                …
              </button>
            ) : (
              <button
                key={p}
                onClick={() => irA(p)}
                className={p === pagina ? 'pag-on num' : 'num'}
                aria-current={p === pagina ? 'page' : undefined}
              >
                {p}
              </button>
            ),
          )}
          <button onClick={() => irA(pagina + 1)} disabled={pagina === paginas} aria-label="Página siguiente">
            ›
          </button>
        </div>
      )}
    </div>
  )
}
